import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import React, { useState } from "react";
import SoftInput from "components/SoftInput";
import { IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Table from "examples/Tables/Table";
import UsersRow, { columns } from "store/action/User";
import useStore from "store/hook";
import { toast } from "react-toastify";
import { FadeLoader } from "react-spinners";
import emptyUsers from "assets/images/users/emptyUsers.png";
import { useGetuserslistQuery } from "store/api/users.api";
import { useParams } from "react-router-dom";
import Formatmessage from "services/Formatmessage";
import useLogout from "services/Handlelogout";

function CityUsersTable({users}) {
  const [usersData, setUsersData] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [regex, setRegex] = React.useState("");
  const [newRegex, setNewRegex] = React.useState("");

  const { id } = useParams();
  const logout = useLogout();

  const debouncedSetRegex = React.useCallback(
    debounce((value) => {
      setNewRegex(value);
    }, 1000),
    []
  );

  React.useEffect(() => {
    debouncedSetRegex(regex);
  }, [regex, debouncedSetRegex]);

  const { data, isLoading, isFetching } = useGetuserslistQuery({
    page: -1,
    limit: 7,
    type: newRegex ? "search" : "list",
    searchText: newRegex,
    uni_filter: [id],
  });

  const { setDialogue } = useStore();

  React.useEffect(() => {
    if (data?.status === 1) {
      setUsersData(data?.data?.users || data?.data || []);
      setTotalUsers(users ?? data?.data?.count ?? data?.data?.totalUsers ?? 0);
    } else if (data?.status == 0) {
      toast.error(Formatmessage(data?.message));
    } else if (data?.status == 10) {
      logout();
    }
  }, [data]);

  const rows = React.useMemo(
    () => (usersData ? usersData?.map((user) => UsersRow(user, setDialogue)) : []),
    [usersData]
  );

  return (
    <SoftBox
      border="1px solid #D8E0E8"
      width="100%"
      borderRadius="md"
      sx={{ backgroundColor: "white.main" }}
    >
      <SoftBox
        sx={{ p: 2 }}
        display="flex"
        flexWrap="wrap"
        gap={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <SoftBox display="flex" sx={{ alignItems: "center", justifyContent: "start", gap: 2 }}>
          <SoftTypography sx={{ fontSize: 16, fontWeight: "medium", color: "black.main" }}>
            Users
          </SoftTypography>

          <SoftBox borderRadius="xl" sx={{ backgroundColor: "skyblue.main", px: 2, py: 0.1 }}>
            <SoftTypography sx={{ fontSize: 16, fontWeight: "medium", color: "primary.main" }}>
              {totalUsers}
            </SoftTypography>
          </SoftBox>
        </SoftBox>

        <SoftBox sx={{ position: "relative", height: "45px", width: { xs: "100%", sm: "230px" } }}>
          <SoftBox
            sx={{
              position: "absolute",
              top: "50%",
              left: "5px",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <IconButton sx={{ transition: "0.3s ease-in-out" }}>
              <SearchIcon sx={{ color: "black.main", width: 20, height: 20 }} />
            </IconButton>
          </SoftBox>

          <SoftInput
            name="search"
            type="text"
            value={regex}
            onChange={(e) => setRegex(e.target.value)}
            placeholder="Search by name"
            fontSize="14px"
            sx={{
              width: "100% !important",
              py: "10px",
              pl: "40px",
            }}
          />
        </SoftBox>
      </SoftBox>

      {isLoading || isFetching ? (
        <SoftBox
          sx={{
            height: "calc(70vh)",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <FadeLoader color="#3DC2EB" />
        </SoftBox>
      ) : (
        <SoftBox>
          {usersData?.length > 0 ? (
            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              <Table columns={columns} rows={rows} />
            </SoftBox>
          ) : (
            <SoftBox
              sx={{
                height: "calc(70vh)",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                justifyContent: "start",
                alignItems: "center",
                gap: 2,
              }}
            >
              <SoftBox
                display="flex"
                flexDirection="column"
                gap={8}
                justifyContent="center"
                alignItems="center"
                width="100%"
              >
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  sx={{ backgroundColor: "skyblue.main" }}
                >
                  {columns.map(({ name, align, width }, index) => (
                    <SoftBox
                      key={index}
                      width={width}
                      align={align}
                      fontWeight="medium"
                      fontSize="14px"
                      py="10px"
                      px="24px"
                      color="black"
                      sx={{ fontWeight: 500, textTransform: "capitalize" }}
                    >
                      {name}
                    </SoftBox>
                  ))}
                </SoftBox>

                <SoftBox display="flex" flexDirection="column" alignItems="center">
                  <SoftBox
                    height="200px"
                    width="200px"
                    borderRadius="lg"
                    sx={{
                      backgroundImage: `url(${emptyUsers})`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                      backgroundSize: "contain",
                    }}
                  />

                  <SoftTypography fontWeight="bold" color="black" sx={{ fontSize: 20 }}>
                    No Users Available
                  </SoftTypography>
                  <SoftTypography fontWeight="medium" color="gray" sx={{ fontSize: 12 }}>
                    We couldn’t find any users for your search. Please refine your keywords or once
                    users sign up, they will appear here.
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          )}
        </SoftBox>
      )}
    </SoftBox>
  );
}

function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
}

export default CityUsersTable;
